'use client';

import { useCallback } from 'react';
import { ConnectedStandardSolanaWallet } from '@privy-io/react-auth/solana';
import { WalletReadyState } from '@solana/wallet-adapter-base';
import { AppKitAdapter } from 'src/utils/AppKitAdapter';
import createPrivyNativeWalletAdapter from 'src/utils/PrivyNativeAdapter';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import {
	VelocityCustomWalletName,
	VELOCITY_CUSTOM_WALLET_OPTIONS,
} from 'src/constants/wallets';
import { useLinkWallet } from './useLinkWallet';
import { Provider } from '@reown/appkit-adapter-solana/react';
import useSyncedRef from '../utils/useSyncedRef';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';

interface AppKitWalletOptions {
	provider: Provider;
	address: string;
}

interface PrivyNativeWalletOptions {
	embeddedWallet: ConnectedStandardSolanaWallet;
}

const useConnectCustomWallet = () => {
	const set = useVelocityStore((s) => s.set);
	const { embeddedWallet } = usePrivyWalletContext();

	const { linkWalletToVelocityClient } = useLinkWallet();
	const linkWalletToVelocityClientRef = useSyncedRef(
		linkWalletToVelocityClient
	); // using a ref to avoid race conditions with the linkWalletToDriftClient callback updates

	const connectToAppKitWallet = useCallback(
		async (appKitOptions: AppKitWalletOptions) => {
			const appKitWallet = new AppKitAdapter(
				appKitOptions.address,
				appKitOptions.provider
			);
			appKitWallet.connected = true;
			appKitWallet.readyState = WalletReadyState.Installed;

			set((state) => {
				if (state.wallet.current.adapter) {
					state.wallet.current.adapter.removeListener(
						'connect',
						linkWalletToVelocityClient
					);
				}
				state.wallet.current.adapter = appKitWallet;
				state.wallet.isAppKit = true;
				state.wallet.isPrivyNativeAuth = false;
			});

			await linkWalletToVelocityClientRef.current();
		},
		[set, linkWalletToVelocityClient]
	);

	const connectToPrivyNative = useCallback(
		async (privyWalletOptions?: PrivyNativeWalletOptions) => {
			const currentEmbeddedWallet =
				privyWalletOptions?.embeddedWallet ?? embeddedWallet;

			if (!currentEmbeddedWallet) {
				throw new Error('No Privy embedded wallet found');
			}

			const privyNativeWallet = createPrivyNativeWalletAdapter(
				currentEmbeddedWallet
			);

			privyNativeWallet.removeListener('connect', linkWalletToVelocityClient);
			privyNativeWallet.on('connect', linkWalletToVelocityClient);

			set((state) => {
				if (state.wallet.current.adapter) {
					state.wallet.current.adapter.removeListener(
						'connect',
						linkWalletToVelocityClient
					);
				}
				state.wallet.current.adapter = privyNativeWallet;
				state.wallet.isAppKit = false;
				state.wallet.isPrivyNativeAuth = true;
			});

			await privyNativeWallet.connect();
		},
		[embeddedWallet, linkWalletToVelocityClient, set]
	);

	const connectWithCustomWallet = useCallback(
		async (
			customWalletName: VelocityCustomWalletName,
			opts?: AppKitWalletOptions | PrivyNativeWalletOptions
		) => {
			switch (customWalletName) {
				case VELOCITY_CUSTOM_WALLET_OPTIONS.APPKIT_WALLET_ADAPTER_NAME:
					return connectToAppKitWallet(opts as AppKitWalletOptions);
				case VELOCITY_CUSTOM_WALLET_OPTIONS.PRIVY_NATIVE_WALLET_ADAPTER_NAME:
					return connectToPrivyNative(opts as PrivyNativeWalletOptions);
				default: {
					throw new Error(`Unhandled custom wallet name: ${customWalletName}`);
				}
			}
		},
		[connectToAppKitWallet, connectToPrivyNative]
	);

	return {
		connectWithCustomWallet,
	};
};

export default useConnectCustomWallet;
